/* =========================================================================
   Shapes — a throwaway comparison surface for skill-tree layout geometries.

   Purpose: the incoming plan is 59 nodes across 11 tracks, which the shipped
   four-quadrant layout cannot express. This page renders ONE shared mock plan
   through each candidate geometry, one per subtab, so they can be compared
   side by side with the same data.

   Fully additive and isolated: it imports nothing from ../DevelopmentPlan and
   nothing imports it except the dashboard route + header entry. Deleting this
   directory and its two route lines removes it without trace.
   ========================================================================= */

import React, { useLayoutEffect, useRef, useState } from 'react';

import { CANDIDATES } from './layouts';
import { MOCK_EDGES, MOCK_NODES, MOCK_TRACKS } from './mock';
import ShapeCanvas from './ShapeCanvas';
import './shapes.css';

/** Measure distance from viewport top and fill the rest — the page never
 *  scrolls (mirrors the Development Plan's fixed-viewport frame). */
const useFillHeight = () => {
  const ref = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number | undefined>(undefined);
  useLayoutEffect(() => {
    const measure = () => {
      if (!ref.current) return;
      const top = ref.current.getBoundingClientRect().top;
      setHeight(Math.max(520, window.innerHeight - top - 10));
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);
  return { ref, height };
};

const Shapes: React.FC = () => {
  const { ref, height } = useFillHeight();
  const [activeKey, setActiveKey] = useState(CANDIDATES[0].key);
  const active = CANDIDATES.find((c) => c.key === activeKey) ?? CANDIDATES[0];

  return (
    <div className="shapes" ref={ref} style={{ height }}>
      <div className="shapes__topbar">
        <div className="shapes__brand">
          <div className="shapes__brand-mark">◇</div>
          <div className="shapes__brand-text">
            <div className="shapes__title">Layout shapes</div>
            <div className="shapes__subtitle">
              {MOCK_NODES.length} nodes · {MOCK_TRACKS.length} tracks · one shared
              mock, five geometries
            </div>
          </div>
        </div>
        <div className="shapes__note">
          Comparison sandbox — not wired to any real plan
        </div>
      </div>

      <div className="shapes__tabs" role="tablist">
        {CANDIDATES.map((c) => (
          <button
            type="button"
            role="tab"
            aria-selected={c.key === activeKey}
            key={c.key}
            className={`shapes__tab${c.key === activeKey ? ' is-active' : ''}`}
            onClick={() => setActiveKey(c.key)}
          >
            {c.label}
          </button>
        ))}
      </div>

      <div className="shapes__caption">
        <span className="shapes__caption-blurb">{active.blurb}</span>
        {!active.placeholder && (
          <span className="shapes__caption-params">{active.params}</span>
        )}
      </div>

      {active.placeholder ? (
        <div className="shapes-placeholder">
          <div className="shapes-placeholder__mark">⊹</div>
          <div className="shapes-placeholder__title">New shape — awaiting spec</div>
          <div className="shapes-placeholder__body">
            Vertical &amp; horizontal connectors only — no diagonals, no long
            radial runs. Describe the layout you have in mind and it will be built
            here against the same {MOCK_TRACKS.length}-track · {MOCK_NODES.length}
            -node mock the other tabs use.
          </div>
        </div>
      ) : (
        <ShapeCanvas
          key={active.key}
          candidate={active}
          nodes={MOCK_NODES}
          tracks={MOCK_TRACKS}
          edges={MOCK_EDGES}
        />
      )}
    </div>
  );
};

export default Shapes;
